import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { hasAnalyticsConsent } from "./CookieConsent";

/**
 * TrackingInjector loads Meta Pixel, TikTok Pixel, GA4, GTM, and Google Ads
 * conversion tag based on settings.tracking from /api/settings/public.
 *
 * Server-side events (CAPI / TikTok Events API) are forwarded via
 * `window.mastofaTrack` which POSTs to /api/track/event.
 *
 * Respects analytics consent from CookieConsent — everything analytics/marketing
 * only loads AFTER user gives consent.
 */
export function TrackingInjector() {
  const location = useLocation();

  // Fetch config + mount pixels ONCE (after consent event too)
  useEffect(() => {
    let done = false;

    const mount = async () => {
      if (done) return;
      if (!hasAnalyticsConsent()) return;
      done = true;
      try {
        const s = await api("/settings/public");
        const t = s?.tracking || {};
        const head = document.head;
        const body = document.body;

        const injectScript = (src, inline = false) => {
          const el = document.createElement("script");
          if (inline) el.text = src;
          else el.src = src;
          el.async = true;
          head.appendChild(el);
        };
        const injectHTML = (html, where = "head") => {
          if (!html) return;
          const container = document.createElement("div");
          container.innerHTML = html;
          Array.from(container.children).forEach((n) =>
            (where === "head" ? head : body).appendChild(n)
          );
        };

        // Meta Pixel
        if (t.meta_pixel_id) {
          injectScript(
            `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window, document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init', '${t.meta_pixel_id}');fbq('track', 'PageView');`,
            true
          );
        }

        // TikTok Pixel
        if (t.tiktok_pixel_id) {
          injectScript(
            `!function (w, d, t) {w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};ttq.load('${t.tiktok_pixel_id}');ttq.page();}(window, document, 'ttq');`,
            true
          );
        }

        // GA4
        if (t.ga4_measurement_id) {
          injectScript(`https://www.googletagmanager.com/gtag/js?id=${t.ga4_measurement_id}`);
          injectScript(
            `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments)}window.gtag=gtag;gtag('js', new Date());gtag('config', '${t.ga4_measurement_id}');`,
            true
          );
        }

        // Google Ads
        if (t.google_ads_conversion_id) {
          injectScript(`https://www.googletagmanager.com/gtag/js?id=${t.google_ads_conversion_id}`);
          injectScript(
            `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments)}window.gtag=gtag;gtag('js', new Date());gtag('config', '${t.google_ads_conversion_id}');`,
            true
          );
        }

        // GTM
        if (t.gtm_container_id) {
          injectScript(
            `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${t.gtm_container_id}');`,
            true
          );
          const noscript = document.createElement("noscript");
          noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${t.gtm_container_id}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
          body.insertBefore(noscript, body.firstChild);
        }

        // Custom
        injectHTML(t.custom_head, "head");
        injectHTML(t.custom_body, "body");
        injectHTML(t.custom_body_end, "body");

        // Global helper
        window.mastofaTrack = async (eventName, params = {}) => {
          try {
            if (window.fbq) window.fbq("track", eventName, params);
            if (window.ttq) window.ttq.track(eventName, params);
            if (window.gtag) window.gtag("event", eventName, params);
          } catch {
            /* ignore client-side */
          }
          // Server-side forward (only for high-intent events to save quota)
          const serverEvents = ["Lead", "Purchase", "InitiateCheckout", "CompleteRegistration", "AddPaymentInfo", "Contact"];
          if (serverEvents.includes(eventName)) {
            try {
              await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track/event`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  event_name: eventName,
                  event_id: params.event_id || crypto.randomUUID?.() || String(Date.now()),
                  event_source_url: window.location.href,
                  email: params.email || "",
                  phone: params.phone || "",
                  external_id: params.external_id || "",
                  value: params.value || 0,
                  currency: params.currency || "IDR",
                  content_ids: params.content_ids || [],
                  content_name: params.content_name || "",
                }),
              });
            } catch {
              /* silent */
            }
          }
        };
      } catch {
        /* ignore */
      }
    };

    mount();
    window.addEventListener("mastofa:consent", mount);
    return () => window.removeEventListener("mastofa:consent", mount);
  }, []);

  // Fire PageView on route change (client-side)
  useEffect(() => {
    if (!hasAnalyticsConsent()) return;
    if (window.fbq) window.fbq("track", "PageView");
    if (window.ttq) window.ttq.page();
    if (window.gtag) window.gtag("event", "page_view", { page_path: location.pathname });
  }, [location.pathname]);

  return null;
}
